import { computed, inject, Injectable, signal } from '@angular/core';
import { NavController } from '@ionic/angular';

import { GlobalConfig } from '@shared/configs';
import { PathAPP } from '@shared/constants';
import { IAuthUser } from '@shared/types';
import { getStorage, removeStorage } from '@shared/utils/storage.util';
/* The file, not the `@core/services` barrel, which imports this package back. */
import { PushNotificationService } from '../services/push-notification.service';

/** Replaces the Zustand `useAuthStore`. */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly nav = inject(NavController);
  private readonly push = inject(PushNotificationService);

  /** In-flight logout, so parallel 401s tear the session down once. */
  private loggingOut: Promise<void> | null = null;

  private readonly _token = signal<string | null>(null);
  private readonly _user = signal<IAuthUser | null>(null);
  private readonly _hydrated = signal(false);

  readonly token = this._token.asReadonly();
  readonly user = this._user.asReadonly();
  readonly hydrated = this._hydrated.asReadonly();

  /** `computed`, so call sites keep the store's `isLoggedIn()` call syntax. */
  readonly isLoggedIn = computed(() => !!this._token());

  login(token: string | null, user: IAuthUser | null): void {
    this._token.set(token);
    this._user.set(user);

    /* Registers this device for the new user if permission is already granted. */
    if (token) {
      void this.push.sync();
    }
  }

  /**
   * Ends the session and lands on the login page.
   *
   * The redirect lives here rather than at the 401 call site so no future
   * logout path can clear the session and leave the user on an authed screen:
   * the guards only run on navigation, so clearing the token alone changes
   * nothing until the user happens to navigate.
   */
  logout(): Promise<void> {
    this.loggingOut ??= this.endSession().finally(() => {
      this.loggingOut = null;
    });

    return this.loggingOut;
  }

  private async endSession(): Promise<void> {
    /* Before the token goes, so the unregister call is still authenticated. */
    await this.push.disable().catch(() => undefined);

    await Promise.all([
      removeStorage(GlobalConfig.KEY_TOKEN),
      removeStorage(GlobalConfig.KEY_USER),
    ]);
    this._token.set(null);
    this._user.set(null);

    /**
     * `navigateRoot`, not `navigateByUrl`: login has to replace the
     * ion-router-outlet stack. A push would leave the expired page behind it,
     * reachable again with the hardware back button or an edge swipe.
     */
    await this.nav.navigateRoot(PathAPP.auth.login);
  }

  /**
   * Runs via `provideAppInitializer`, so token/user are populated before any
   * guard or component reads them. React deferred this to a `hydrated` flag
   * and rendered the string "Loading..." inside the router outlet meanwhile.
   */
  async initAuth(): Promise<void> {
    const [token, user] = await Promise.all([
      getStorage(GlobalConfig.KEY_TOKEN),
      getStorage(GlobalConfig.KEY_USER),
    ]);

    this._user.set(user ? (JSON.parse(user) as IAuthUser) : null);
    this._token.set(token);
    this._hydrated.set(true);

    /* Not awaited: bootstrap must never wait on the push service worker. */
    void this.push.init().then(() => (token ? this.push.sync() : undefined));
  }
}
